import axios from 'axios'
import { toServiceCriterion, isEmptyCriterion } from '../../utils/criterionUtils'

// Helper function to extract count from various response formats
function extractCount(data) {
    if (data == null) return null

    // Direct count property
    if (typeof data.count === 'number') return data.count

    // Audsum format: { data: { header: ["count"], body: [[63557]] } }
    if (data.data && data.data.body && Array.isArray(data.data.body) && data.data.body.length > 0) {
        if (Array.isArray(data.data.body[0]) && data.data.body[0].length > 0) {
            return data.data.body[0][0]
        }
    }

    // Response array format: { response: [{ count: 123 }] }
    if (data.response && Array.isArray(data.response) && data.response.length > 0) {
        if (typeof data.response[0].count === 'number') return data.response[0].count
        if (typeof data.response[0] === 'number') return data.response[0]
    }

    // Result property: { result: { count: 123 } }
    if (data.result && typeof data.result.count === 'number') return data.result.count

    // Data array format: { data: [{ count: 123 }] }
    if (data.data && Array.isArray(data.data) && data.data.length > 0) {
        if (typeof data.data[0].count === 'number') return data.data[0].count
    }

    // Rows format: { rows: [[123]] }
    if (data.rows && Array.isArray(data.rows) && data.rows.length > 0) {
        if (Array.isArray(data.rows[0]) && data.rows[0].length > 0) {
            return data.rows[0][0]
        }
    }

    return null
}

const store = {
    namespaced: true,
    state: {
        audiences: [],
        currentAudience: null,
        dataEnvironments: [],
        currentDataEnvironment: null,
        datasets: [],
        currentDataset: null,
        distributions: [],
        currentDistribution: null,
        distributionDestinations: [],
        executionHistory: [],
        currentExecution: null,
        exportHistory: [],
        histogramData: null,
        audienceCount: null,
        isLoadingCount: false,
        isLoadingHistogram: false,
    },
    mutations: {
        setAudiences(state, audiences) {
            state.audiences = audiences
        },
        setCurrentAudience(state, audience) {
            state.currentAudience = audience
        },
        setDataEnvironments(state, dataEnvironments) {
            state.dataEnvironments = dataEnvironments
        },
        setCurrentDataEnvironment(state, dataEnvironment) {
            state.currentDataEnvironment = dataEnvironment
        },
        setDatasets(state, datasets) {
            state.datasets = datasets
        },
        setCurrentDataset(state, dataset) {
            state.currentDataset = dataset
        },
        setDistributions(state, distributions) {
            state.distributions = distributions
        },
        setCurrentDistribution(state, distribution) {
            state.currentDistribution = distribution
        },
        setDistributionDestinations(state, destinations) {
            state.distributionDestinations = destinations
        },
        setExecutionHistory(state, history) {
            state.executionHistory = history
        },
        setCurrentExecution(state, execution) {
            state.currentExecution = execution
        },
        setExportHistory(state, history) {
            state.exportHistory = history
        },
        setHistogramData(state, data) {
            state.histogramData = data
        },
        setAudienceCount(state, count) {
            state.audienceCount = count
        },
        setIsLoadingCount(state, loading) {
            state.isLoadingCount = loading
        },
        setIsLoadingHistogram(state, loading) {
            state.isLoadingHistogram = loading
        },
    },
    actions: {
        // Reset state when client changes
        onClientChange({ commit }) {
            commit('setAudiences', [])
            commit('setCurrentAudience', null)
            commit('setDataEnvironments', [])
            commit('setCurrentDataEnvironment', null)
            commit('setDatasets', [])
            commit('setCurrentDataset', null)
            commit('setDistributions', [])
            commit('setCurrentDistribution', null)
            commit('setExecutionHistory', [])
            commit('setExportHistory', [])
            commit('setHistogramData', null)
            commit('setAudienceCount', null)
        },

        onAudienceChange({ commit }) {
            commit('setExecutionHistory', [])
            commit('setExportHistory', [])
            commit('setDistributions', [])
            commit('setCurrentExecution', null)
            commit('setHistogramData', null)
            commit('setAudienceCount', null)
        },

        // Set current audience
        setCurrentAudience({ commit }, audience) {
            commit('setCurrentAudience', audience)
        },

        // Set current distribution
        setCurrentDistribution({ commit }, distribution) {
            commit('setCurrentDistribution', distribution)
        },

        // Fetch all audiences for the current client
        async fetchAudiences({ rootState, commit }) {
            try {
                const response = await axios.get(
                    `${rootState.surfaceUrl}/v2/cids/${rootState.currentClient.cid}/audiences`,
                    {
                        headers: {
                            Authorization: `Bearer ${rootState.accessToken}`
                        }
                    }
                )
                const audiences = response.data.audience || response.data || []
                commit('setAudiences', audiences)
                return audiences
            } catch (error) {
                console.error('Error fetching audiences:', error)
                commit('setAudiences', [])
                throw error
            }
        },

        // Fetch a single audience by Name
        async fetchAudienceByName({ rootState, commit }, audienceName) {
            try {
                const response = await axios.get(
                    `${rootState.surfaceUrl}/v2/cids/${rootState.currentClient.cid}/audiences/${audienceName}`,
                    {
                        headers: {
                            Authorization: `Bearer ${rootState.accessToken}`
                        }
                    }
                )
                commit('setCurrentAudience', response.data)
                return response.data
            } catch (error) {
                console.error('Error fetching audience:', error)
                throw error
            }
        },

        // Create a new audience
        async createAudience({ rootState, dispatch }, audienceData) {
            try {
                const response = await axios.post(
                    `${rootState.surfaceUrl}/v2/cids/${rootState.currentClient.cid}/audiences`,
                    audienceData,
                    {
                        headers: {
                            Authorization: `Bearer ${rootState.accessToken}`
                        }
                    }
                )
                await dispatch('fetchAudiences')
                return response.data
            } catch (error) {
                console.error('Error creating audience:', error)
                throw error
            }
        },

        // Update an existing audience
        async updateAudience({ rootState, dispatch }, { audienceName, audienceData }) {
            try {
                const response = await axios.put(
                    `${rootState.surfaceUrl}/v2/cids/${rootState.currentClient.cid}/audiences/${audienceName}`,
                    audienceData,
                    {
                        headers: {
                            Authorization: `Bearer ${rootState.accessToken}`
                        }
                    }
                )
                await dispatch('fetchAudiences')
                return response.data
            } catch (error) {
                console.error('Error updating audience:', error)
                throw error
            }
        },

        // Delete an audience
        async deleteAudience({ rootState, dispatch }, audienceName) {
            try {
                await axios.delete(
                    `${rootState.surfaceUrl}/v2/cids/${rootState.currentClient.cid}/audiences/${audienceName}`,
                    {
                        headers: {
                            Authorization: `Bearer ${rootState.accessToken}`
                        }
                    }
                )
                await dispatch('fetchAudiences')
            } catch (error) {
                console.error('Error deleting audience:', error)
                throw error
            }
        },

        // Fetch data environments (datasets are nested within each environment)
        async fetchDataEnvironments({ rootState, commit }) {
            try {
                const response = await axios.get(
                    `${rootState.audsumUrl}/audience/v1/cids/${rootState.currentClient.cid}/data_environments`,
                    {
                        headers: {
                            Authorization: `Bearer ${rootState.accessToken}`
                        }
                    }
                )
                // Response format: array of { name: "env_name", datasets: ["dataset1", "dataset2", ...] }
                const environments = response.data.data_environment || response.data || []
                commit('setDataEnvironments', environments)
                return environments
            } catch (error) {
                console.error('Error fetching data environments:', error)
                commit('setDataEnvironments', [])
                throw error
            }
        },

        // Get datasets for a data environment (from already fetched data environments)
        getDatasets({ state, commit }, dataEnvironmentName) {
            const env = state.dataEnvironments.find(e =>
                e.name === dataEnvironmentName || e.data_environment === dataEnvironmentName || e === dataEnvironmentName
            )
            let datasets = []
            if (env) {
                // Datasets are nested in the environment object
                datasets = env.datasets || []
            }
            commit('setDatasets', datasets)
            return datasets
        },

        // Fetch datasets for a data environment (legacy - calls separate endpoint if needed)
        async fetchDatasets({ rootState, state, commit }, dataEnvironmentId) {
            // First try to get from already loaded data environments
            const env = state.dataEnvironments.find(e =>
                e.name === dataEnvironmentId || e.data_environment === dataEnvironmentId || e === dataEnvironmentId
            )
            if (env && env.datasets) {
                commit('setDatasets', env.datasets)
                return env.datasets
            }

            // Fallback to API call if needed
            try {
                const response = await axios.get(
                    `${rootState.audsumUrl}/audience/v1/cids/${rootState.currentClient.cid}/data_environments/${dataEnvironmentId}/datasets`,
                    {
                        headers: {
                            Authorization: `Bearer ${rootState.accessToken}`
                        }
                    }
                )
                const datasets = response.data.dataset || response.data || []
                commit('setDatasets', datasets)
                return datasets
            } catch (error) {
                console.error('Error fetching datasets:', error)
                commit('setDatasets', [])
                throw error
            }
        },

        // Poll for execution result
        async pollExecutionResult({ rootState }, { dataEnvironmentId, dataset, executionId, maxAttempts = 30, interval = 2000 }) {
            for (let attempt = 0; attempt < maxAttempts; attempt++) {
                const response = await axios.get(
                    `${rootState.audsumUrl}/audience/v1/cids/${rootState.currentClient.cid}/data_environments/${dataEnvironmentId}/datasets/${dataset}/executions/${executionId}`,
                    {
                        headers: {
                            Authorization: `Bearer ${rootState.accessToken}`
                        }
                    }
                )
                const status = (response.data.status || '').toUpperCase()
                console.log(`Polling execution ${executionId}, attempt ${attempt + 1}, status: ${status}`, response.data)

                // Per audsum spec: status can be SUCCESS, FAILED, or RUNNING
                if (status === 'SUCCESS' || status === 'SUCCEEDED' || status === 'COMPLETED') {
                    return response.data
                }
                if (status === 'FAILED' || status === 'ERROR') {
                    throw new Error(response.data.reason || response.data.error_message || response.data.message || 'Query execution failed')
                }
                // Status is RUNNING or PENDING, wait and retry
                await new Promise(resolve => setTimeout(resolve, interval))
            }
            throw new Error('Query execution timed out')
        },

        // Run a count query for an audience
        async runCountQuery({ rootState, commit, dispatch }, { dataEnvironmentId, queryBody, queryEngine = 'analytics' }) {
            commit('setIsLoadingCount', true)
            commit('setAudienceCount', null)
            try {
                const dataset = queryBody.dataset
                // Per audsum spec: POST .../datasets/{ds}/count with criterion in body
                // the query endpoints do not know the 'equal' operator the record
                // stores - send equality as a single-value 'in'. An empty
                // criterion is left out entirely: `criterion: {}` is an error,
                // no key at all means the whole dataset.
                const requestBody = {}
                if (!isEmptyCriterion(queryBody.criterion)) {
                    requestBody.criterion = toServiceCriterion(queryBody.criterion)
                }
                console.log('Running count query:', { dataEnvironmentId, dataset, requestBody })

                const response = await axios.post(
                    `${rootState.audsumUrl}/audience/v1/cids/${rootState.currentClient.cid}/data_environments/${dataEnvironmentId}/datasets/${dataset}/count?query_engine=${queryEngine}`,
                    requestBody,
                    {
                        headers: {
                            Authorization: `Bearer ${rootState.accessToken}`
                        }
                    }
                )

                console.log('Count query response:', response.data)

                // Check if we have an execution_id - need to handle both running and already completed
                const status = (response.data.status || '').toUpperCase()
                if (response.data.execution_id) {
                    // If status is RUNNING/PENDING/QUEUED, poll for completion
                    if (status === 'RUNNING' || status === 'PENDING' || status === 'QUEUED') {
                        console.log('Async execution running, polling for results...')
                        const result = await dispatch('pollExecutionResult', {
                            dataEnvironmentId,
                            dataset,
                            executionId: response.data.execution_id
                        })
                        console.log('Poll result:', result)
                        const count = extractCount(result)
                        commit('setAudienceCount', count)
                        return result
                    }
                    // If status is already SUCCESS/COMPLETED, fetch the execution result directly
                    if (status === 'SUCCESS' || status === 'SUCCEEDED' || status === 'COMPLETED') {
                        console.log('Execution already completed, fetching results...')
                        const result = await dispatch('fetchExecutionResult', {
                            dataEnvironmentId,
                            dataset,
                            executionId: response.data.execution_id
                        })
                        console.log('Execution result:', result)
                        const count = extractCount(result)
                        commit('setAudienceCount', count)
                        return result
                    }
                }

                // Synchronous response - extract count from various possible formats
                const count = extractCount(response.data)
                console.log('Extracted count:', count)
                commit('setAudienceCount', count)
                return response.data
            } catch (error) {
                console.error('Error running count query:', error)
                throw error
            } finally {
                commit('setIsLoadingCount', false)
            }
        },

        // Run a histogram query
        async runHistogramQuery({ rootState, commit, dispatch }, { dataEnvironmentId, queryBody, queryEngine = 'analytics' }) {
            commit('setIsLoadingHistogram', true)
            commit('setHistogramData', null)
            try {
                const dataset = queryBody.dataset
                // Per audsum spec: POST .../datasets/{ds}/histogram
                // Empty body = get all attributes
                const requestBody = {}

                // Add criterion only if provided
                // an empty criterion is omitted - `criterion: {}` is rejected
                if (!isEmptyCriterion(queryBody.criterion)) {
                    requestBody.criterion = toServiceCriterion(queryBody.criterion)
                }
                // Support both single column (attribute_names with one item) and multiple columns
                if (queryBody.attribute_names) {
                    requestBody.attribute_names = queryBody.attribute_names
                } else if (queryBody.histogram_column || queryBody.column_name) {
                    // Legacy support for single column
                    requestBody.attribute_names = [queryBody.histogram_column || queryBody.column_name]
                }
                if (queryBody.limit) {
                    requestBody.limit = queryBody.limit
                }

                const response = await axios.post(
                    `${rootState.audsumUrl}/audience/v1/cids/${rootState.currentClient.cid}/data_environments/${dataEnvironmentId}/datasets/${dataset}/histogram?query_engine=${queryEngine}`,
                    requestBody,
                    {
                        headers: {
                            Authorization: `Bearer ${rootState.accessToken}`
                        }
                    }
                )

                // Check if we have an execution_id - need to handle both running and already completed
                const status = (response.data.status || '').toUpperCase()
                if (response.data.execution_id) {
                    // If status is RUNNING/PENDING/QUEUED, poll for completion
                    if (status === 'RUNNING' || status === 'PENDING' || status === 'QUEUED') {
                        const result = await dispatch('pollExecutionResult', {
                            dataEnvironmentId,
                            dataset,
                            executionId: response.data.execution_id
                        })
                        commit('setHistogramData', result)
                        return result
                    }
                    // If status is already SUCCESS/COMPLETED, fetch the execution result directly
                    if (status === 'SUCCESS' || status === 'SUCCEEDED' || status === 'COMPLETED') {
                        const result = await dispatch('fetchExecutionResult', {
                            dataEnvironmentId,
                            dataset,
                            executionId: response.data.execution_id
                        })
                        commit('setHistogramData', result)
                        return result
                    }
                }

                commit('setHistogramData', response.data)
                return response.data
            } catch (error) {
                console.error('Error running histogram query:', error)
                throw error
            } finally {
                commit('setIsLoadingHistogram', false)
            }
        },

        // Run an index values query for top indexing analysis
        async runIndexValuesQuery({ rootState, dispatch }, { dataEnvironmentId, queryBody, queryEngine = 'analytics' }) {
            try {
                const dataset = queryBody.dataset
                const requestBody = {}

                // Add criterion (required for index values)
                // an empty criterion is omitted - `criterion: {}` is rejected
                if (!isEmptyCriterion(queryBody.criterion)) {
                    requestBody.criterion = toServiceCriterion(queryBody.criterion)
                }

                // Add optional attribute_names to limit which columns to analyze
                if (queryBody.attribute_names && queryBody.attribute_names.length > 0) {
                    requestBody.attribute_names = queryBody.attribute_names
                }

                console.log('Running index values query:', { dataEnvironmentId, dataset, requestBody })

                const response = await axios.post(
                    `${rootState.audsumUrl}/audience/v1/cids/${rootState.currentClient.cid}/data_environments/${dataEnvironmentId}/datasets/${dataset}/index_values?query_engine=${queryEngine}`,
                    requestBody,
                    {
                        headers: {
                            Authorization: `Bearer ${rootState.accessToken}`
                        }
                    }
                )

                console.log('Index values initial response:', response.data)

                // Check if we have an execution_id - need to handle async execution
                const status = (response.data.status || '').toUpperCase()
                if (response.data.execution_id) {
                    // If status is already SUCCESS/COMPLETED with data, return it
                    if ((status === 'SUCCESS' || status === 'SUCCEEDED' || status === 'COMPLETED') && response.data.data) {
                        return response.data
                    }
                    // Otherwise poll for completion (RUNNING, PENDING, QUEUED, or no status)
                    console.log('Polling for index values results, execution_id:', response.data.execution_id)
                    const result = await dispatch('pollIndexValuesResult', {
                        dataEnvironmentId,
                        dataset,
                        executionId: response.data.execution_id
                    })
                    return result
                }

                return response.data
            } catch (error) {
                console.error('Error running index values query:', error)
                throw error
            }
        },

        // Poll for index values execution result
        async pollIndexValuesResult({ rootState }, { dataEnvironmentId, dataset, executionId, maxAttempts = 60, interval = 2000 }) {
            console.log(`Polling index values execution ${executionId}`)
            for (let attempt = 0; attempt < maxAttempts; attempt++) {
                try {
                    const response = await axios.get(
                        `${rootState.audsumUrl}/audience/v1/cids/${rootState.currentClient.cid}/data_environments/${dataEnvironmentId}/datasets/${dataset}/index_values/executions/${executionId}`,
                        {
                            headers: {
                                Authorization: `Bearer ${rootState.accessToken}`
                            }
                        }
                    )

                    const status = (response.data.status || '').toUpperCase()
                    console.log(`Index values poll attempt ${attempt + 1}, status: ${status}`)

                    if (status === 'SUCCESS' || status === 'SUCCEEDED' || status === 'COMPLETED') {
                        console.log('Index values results:', response.data)
                        return response.data
                    }
                    if (status === 'FAILED' || status === 'ERROR') {
                        throw new Error(response.data.reason || response.data.error_message || response.data.message || 'Index values query failed')
                    }

                    // Still running, wait and retry
                    await new Promise(resolve => setTimeout(resolve, interval))
                } catch (error) {
                    if (error.response?.status === 404) {
                        // Execution not ready yet, wait and retry
                        console.log(`Index values execution ${executionId} not found yet, retrying...`)
                        await new Promise(resolve => setTimeout(resolve, interval))
                    } else {
                        throw error
                    }
                }
            }
            throw new Error('Index values query execution timed out')
        },

        // Fetch distributions for an audience
        async fetchDistributions({ rootState, commit }, audienceName) {
            try {
                const response = await axios.get(
                    `${rootState.surfaceUrl}/v2/cids/${rootState.currentClient.cid}/audiences/${audienceName}/audience_distributions`,
                    {
                        headers: {
                            Authorization: `Bearer ${rootState.accessToken}`
                        }
                    }
                )
                const distributions = response.data.audience_distribution || response.data || []
                commit('setDistributions', distributions)
                return distributions
            } catch (error) {
                console.error('Error fetching distributions:', error)
                commit('setDistributions', [])
                throw error
            }
        },

        // Create a distribution
        async createDistribution({ rootState, dispatch }, { audienceName, distributionData }) {
            try {
                const response = await axios.post(
                    `${rootState.surfaceUrl}/v2/cids/${rootState.currentClient.cid}/audiences/${audienceName}/audience_distributions`,
                    distributionData,
                    {
                        headers: {
                            Authorization: `Bearer ${rootState.accessToken}`
                        }
                    }
                )
                await dispatch('fetchDistributions', audienceName)
                return response.data
            } catch (error) {
                console.error('Error creating distribution:', error)
                throw error
            }
        },

        // Update a distribution
        async updateDistribution({ rootState, dispatch }, { audienceName, distributionName, distributionData }) {
            try {
                const response = await axios.put(
                    `${rootState.surfaceUrl}/v2/cids/${rootState.currentClient.cid}/audiences/${audienceName}/audience_distributions/${distributionName}`,
                    distributionData,
                    {
                        headers: {
                            Authorization: `Bearer ${rootState.accessToken}`
                        }
                    }
                )
                await dispatch('fetchDistributions', audienceName)
                return response.data
            } catch (error) {
                console.error('Error updating distribution:', error)
                throw error
            }
        },

        // Delete a distribution
        async deleteDistribution({ rootState, dispatch }, { audienceName, distributionName }) {
            try {
                await axios.delete(
                    `${rootState.surfaceUrl}/v2/cids/${rootState.currentClient.cid}/audiences/${audienceName}/audience_distributions/${distributionName}`,
                    {
                        headers: {
                            Authorization: `Bearer ${rootState.accessToken}`
                        }
                    }
                )
                await dispatch('fetchDistributions', audienceName)
            } catch (error) {
                console.error('Error deleting distribution:', error)
                throw error
            }
        },

        // Fetch distribution destinations
        async fetchDistributionDestinations({ rootState, commit }) {
            try {
                const response = await axios.get(
                    `${rootState.surfaceUrl}/v2/cids/${rootState.currentClient.cid}/distribution_destinations`,
                    {
                        headers: {
                            Authorization: `Bearer ${rootState.accessToken}`
                        }
                    }
                )
                const destinations = response.data.distribution_destination || response.data || []
                commit('setDistributionDestinations', destinations)
                return destinations
            } catch (error) {
                console.error('Error fetching distribution destinations:', error)
                commit('setDistributionDestinations', [])
                throw error
            }
        },

        // Fetch distribution history
        async fetchDistributionHistory({ rootState }, { audienceName, distributionName }) {
            try {
                const response = await axios.get(
                    `${rootState.surfaceUrl}/v2/cids/${rootState.currentClient.cid}/audiences/${audienceName}/audience_distributions/${distributionName}/history`,
                    {
                        headers: {
                            Authorization: `Bearer ${rootState.accessToken}`
                        }
                    }
                )
                return response.data.distribution_history || response.data || []
            } catch (error) {
                console.error('Error fetching distribution history:', error)
                throw error
            }
        },

        // Fetch execution history for an audience
        async fetchExecutionHistory({ rootState, commit }, { dataEnvironmentId, dataset }) {
            try {
                const response = await axios.get(
                    `${rootState.audsumUrl}/audience/v1/cids/${rootState.currentClient.cid}/data_environments/${dataEnvironmentId}/datasets/${dataset}/executions`,
                    {
                        headers: {
                            Authorization: `Bearer ${rootState.accessToken}`
                        }
                    }
                )
                const executions = response.data.executions || response.data || []
                commit('setExecutionHistory', executions)
                return executions
            } catch (error) {
                console.error('Error fetching execution history:', error)
                commit('setExecutionHistory', [])
                throw error
            }
        },

        // Fetch execution result by ID
        async fetchExecutionResult({ rootState }, { dataEnvironmentId, dataset, executionId }) {
            try {
                const response = await axios.get(
                    `${rootState.audsumUrl}/audience/v1/cids/${rootState.currentClient.cid}/data_environments/${dataEnvironmentId}/datasets/${dataset}/executions/${executionId}`,
                    {
                        headers: {
                            Authorization: `Bearer ${rootState.accessToken}`
                        }
                    }
                )
                return response.data
            } catch (error) {
                console.error('Error fetching execution result:', error)
                throw error
            }
        },

        // Run user ID export query
        async runUserIdQuery({ rootState, dispatch }, { dataEnvironmentId, queryBody }) {
            try {
                const dataset = queryBody.dataset
                // Per audsum spec: POST .../datasets/{ds}/user_ids with criterion, limit, id_type
                const requestBody = {}
                // an empty criterion is omitted - `criterion: {}` is rejected
                if (!isEmptyCriterion(queryBody.criterion)) {
                    requestBody.criterion = toServiceCriterion(queryBody.criterion)
                }
                if (queryBody.output_location) {
                    requestBody.output_location = queryBody.output_location
                }
                if (queryBody.user_id_columns) {
                    requestBody.user_id_columns = [{column_names: queryBody.user_id_columns}]
                }

                const response = await axios.post(
                    `${rootState.audsumUrl}/audience/v1/cids/${rootState.currentClient.cid}/data_environments/${dataEnvironmentId}/datasets/${dataset}/user_ids`,
                    requestBody,
                    {
                        headers: {
                            Authorization: `Bearer ${rootState.accessToken}`
                        }
                    }
                )

                // Check if we have an execution_id - need to handle both running and already completed
                const status = (response.data.status || '').toUpperCase()
                if (response.data.execution_id) {
                    // If status is RUNNING/PENDING/QUEUED, poll for completion
                    if (status === 'RUNNING' || status === 'PENDING' || status === 'QUEUED') {
                        const result = await dispatch('pollExecutionResult', {
                            dataEnvironmentId,
                            dataset,
                            executionId: response.data.execution_id
                        })
                        return result
                    }
                    // If status is already SUCCESS/COMPLETED, fetch the execution result directly
                    if (status === 'SUCCESS' || status === 'SUCCEEDED' || status === 'COMPLETED') {
                        const result = await dispatch('fetchExecutionResult', {
                            dataEnvironmentId,
                            dataset,
                            executionId: response.data.execution_id
                        })
                        return result
                    }
                }

                return response.data
            } catch (error) {
                console.error('Error running user ID query:', error)
                throw error
            }
        },

        // Save aggregate audience (Union or Intersect)
        async saveAggregateAudience({ rootState, dispatch }, { audiences, audienceName, type }) {
            try {
                // Build criterion from all selected audiences
                const criterions = audiences.map(a => JSON.parse(a.audience_criterion_json || '{}'))
                const parentAudienceNames = audiences.map(a => a.audience_name).join(',')

                // Get dataset info from first audience
                const firstAudience = audiences[0]
                const audsum_dataset = firstAudience.audsum_dataset
                const audsum_data_environment = firstAudience.audsum_data_environment

                // Build aggregate query
                const operator = type === 'Union' ? 'or' : 'and'
                const operands = criterions.map(c => c.criterion || c)
                const query = {
                    criterion: {
                        operator,
                        operands
                    }
                }

                const audienceData = {
                    audience_name: audienceName,
                    audsum_dataset,
                    audsum_data_environment,
                    audience_criterion_json: JSON.stringify(query),
                    parent_audiences: parentAudienceNames,
                }

                const response = await axios.post(
                    `${rootState.surfaceUrl}/v2/cids/${rootState.currentClient.cid}/audiences`,
                    audienceData,
                    {
                        headers: {
                            Authorization: `Bearer ${rootState.accessToken}`
                        }
                    }
                )
                await dispatch('fetchAudiences')
                return response.data
            } catch (error) {
                console.error('Error saving aggregate audience:', error)
                throw error
            }
        },

        // Subtract audience
        async subtractAudience({ rootState, dispatch }, { targetAudience, subtractAudiences, audienceName }) {
            try {
                // Build criterion: target AND NOT (subtract1 OR subtract2 OR ...)
                const targetCriterion = JSON.parse(targetAudience.audience_criterion_json || '{}')
                const subtractCriterions = subtractAudiences.map(a => JSON.parse(a.audience_criterion_json || '{}'))

                // Build subtract operands
                const subtractOperands = subtractCriterions.map(c => c.criterion || c)
                const subtractPart = subtractOperands.length === 1
                    ? subtractOperands[0]
                    : { operator: 'or', operands: subtractOperands }

                // Build final query: target AND NOT subtract
                const query = {
                    criterion: {
                        operator: 'and',
                        operands: [
                            targetCriterion.criterion || targetCriterion,
                            {
                                operator: 'not',
                                operands: [subtractPart]
                            }
                        ]
                    }
                }

                // Build parent_audiences list (target first, then subtract audiences)
                const parentAudienceNames = [targetAudience.audience_name, ...subtractAudiences.map(a => a.audience_name)].join(',')

                const audienceData = {
                    audience_name: audienceName,
                    audsum_dataset: targetAudience.audsum_dataset,
                    audsum_data_environment: targetAudience.audsum_data_environment,
                    audience_criterion_json: JSON.stringify(query),
                    parent_audiences: parentAudienceNames,
                }

                const response = await axios.post(
                    `${rootState.surfaceUrl}/v2/cids/${rootState.currentClient.cid}/audiences`,
                    audienceData,
                    {
                        headers: {
                            Authorization: `Bearer ${rootState.accessToken}`
                        }
                    }
                )
                await dispatch('fetchAudiences')
                return response.data
            } catch (error) {
                console.error('Error subtracting audience:', error)
                throw error
            }
        },

        // Fetch schema for a dataset (returns dataset details including schema)
        async fetchSchema({ rootState }, { dataEnvironmentId, dataset }) {
            try {
                // Per audsum spec: GET /data_environments/{de}/datasets/{ds} returns { name, schema: { columns: [...] } }
                const response = await axios.get(
                    `${rootState.audsumUrl}/audience/v1/cids/${rootState.currentClient.cid}/data_environments/${dataEnvironmentId}/datasets/${dataset}`,
                    {
                        headers: {
                            Authorization: `Bearer ${rootState.accessToken}`
                        }
                    }
                )
                return response.data
            } catch (error) {
                console.error('Error fetching schema:', error)
                throw error
            }
        },

        // Fetch distinct column values using histogram endpoint
        async fetchColumnValues({ rootState, dispatch }, { dataEnvironmentId, dataset, column }) {
            try {
                // Per audsum spec: POST .../datasets/{ds}/histogram with attribute_names array
                // No criterion at all - this asks for every value of the column
                const requestBody = {
                    attribute_names: [column],
                }
                console.log('Fetching column values:', { dataEnvironmentId, dataset, column, requestBody })

                const response = await axios.post(
                    `${rootState.audsumUrl}/audience/v1/cids/${rootState.currentClient.cid}/data_environments/${dataEnvironmentId}/datasets/${dataset}/histogram`,
                    requestBody,
                    {
                        headers: {
                            Authorization: `Bearer ${rootState.accessToken}`
                        }
                    }
                )

                console.log('Histogram response:', response.data)

                // Check if we have an execution_id - need to handle both running and already completed
                const status = (response.data.status || '').toUpperCase()
                if (response.data.execution_id) {
                    // If status is RUNNING/PENDING/QUEUED, poll for completion
                    if (status === 'RUNNING' || status === 'PENDING' || status === 'QUEUED') {
                        const result = await dispatch('pollExecutionResult', {
                            dataEnvironmentId,
                            dataset,
                            executionId: response.data.execution_id
                        })
                        return result
                    }
                    // If status is already SUCCESS/COMPLETED, fetch the execution result directly
                    if (status === 'SUCCESS' || status === 'SUCCEEDED' || status === 'COMPLETED') {
                        const result = await dispatch('fetchExecutionResult', {
                            dataEnvironmentId,
                            dataset,
                            executionId: response.data.execution_id
                        })
                        return result
                    }
                }

                return response.data
            } catch (error) {
                console.error('Error fetching column values:', error)
                throw error
            }
        },

        async insertAudienceExecution({ rootState }, { audienceName, requestBody }) {
            try {
                await axios.post(
                    `${rootState.surfaceUrl}/v2/cids/${rootState.currentClient.cid}/audiences/${audienceName}/audience_executions`,
                    requestBody,
                    {
                        headers: {
                            Authorization: `Bearer ${rootState.accessToken}`
                        }
                    }
                )
            }
            catch (error) {
                console.error('Error inserting audience execution:', error)
                throw error
            }
        },

        // Fetch export history for an audience
        async fetchExportHistory({ rootState, commit }, { audienceName }) {
            try {
                const response = await axios.get(
                    `${rootState.surfaceUrl}/v2/cids/${rootState.currentClient.cid}/audiences/${audienceName}/audience_executions`,
                    {
                        headers: {
                            Authorization: `Bearer ${rootState.accessToken}`
                        }
                    }
                )
                const exports = response.data.audience_executions || response.data || []
                commit('setExportHistory', exports)
                return exports
            } catch (error) {
                console.error('Error fetching export history:', error)
                commit('setExportHistory', [])
                throw error
            }
        },
    },
    getters: {
        audienceByName: (state) => (name) => {
            return state.audiences.find(a => a.audience_name === name)
        },
        simpleAudiences: (state) => {
            return state.audiences.filter(a => !a.parent_audiences || a.parent_audiences === '')
        },
        aggregateAudiences: (state) => {
            return state.audiences.filter(a => a.parent_audiences && a.parent_audiences !== '')
        }
    }
}

export default store
